import { useEffect, useRef, useState } from 'react';
import { api } from '../services/api';

/**
 * Snapshot returned by `useProvidersOgCounts`.
 *
 * Render branches:
 *   - `isLoading`                — counts fetch in flight (combobox
 *                                  options render without badges)
 *   - `error`                    — counts fetch failed (combobox options
 *                                  render without badges; we don't block
 *                                  the dropdown on a count failure)
 *   - `counts === null`          — initial state before the first fetch
 *                                  resolves
 *   - otherwise                  — render CountBadges next to options
 */
export interface UseProvidersOgCountsResult {
  readonly counts: Readonly<Record<string, number>> | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
}

/**
 * Per-OG count fetcher backing the Providers OG combobox badges.
 *
 * 2026-06-05 (Maria) extension: now respects ALL active filters, not
 * just metroId. The hook re-fires whenever `metroId` OR `search`
 * changes so the badges stay in sync with the table results.
 *
 * Behavior:
 *   - Fires GET /api/providers/og-counts when either input changes.
 *     metroId may be empty (universe-wide counts); search may be empty
 *     (no name narrowing). Both empty is also valid -- counts across
 *     all providers.
 *   - Caches by composite key `metroId|search` in a Map so swapping
 *     back to a previously-seen filter set doesn't refire. Cache is
 *     per-hook-instance (per-page lifetime).
 *   - Stale-resolution guard: a generation counter discards in-flight
 *     responses whose inputs have since changed, preventing a slow
 *     Houston fetch from clobbering a fresh Denver value.
 *
 * Errors don't surface to the user -- the dropdown simply renders
 * without badges (graceful degradation; the filter still works).
 */
export function useProvidersOgCounts(
  metroId: string,
  search: string = '',
): UseProvidersOgCountsResult {
  const [counts, setCounts] = useState<Readonly<Record<string, number>> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  // Per (metroId, search) cache shared across this hook instance's
  // lifetime. useRef so React doesn't reset it on re-render.
  const cacheRef = useRef<Map<string, Readonly<Record<string, number>>>>(new Map());

  // Race guard: bumped on every input change; in-flight resolutions
  // check their captured `gen` and drop on the floor if stale.
  const generationRef = useRef<number>(0);

  useEffect(() => {
    generationRef.current += 1;
    const gen = generationRef.current;
    const cacheKey = `${metroId}|${search}`;

    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setCounts(cached);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    // NOTE: don't clear counts here so the dropdown doesn't flicker on
    // filter changes -- slightly stale badges for ~100ms are less
    // jarring than a sudden disappearance + reappearance.

    api.providers
      .ogCounts({
        metroId: metroId || undefined,
        search: search || undefined,
      })
      .then((res) => {
        if (gen !== generationRef.current) return;
        cacheRef.current.set(cacheKey, res.counts);
        setCounts(res.counts);
      })
      .catch((cause: unknown) => {
        if (gen !== generationRef.current) return;
        setError(cause instanceof Error ? cause : new Error(String(cause)));
        setCounts(null);
      })
      .finally(() => {
        if (gen !== generationRef.current) return;
        setIsLoading(false);
      });
  }, [metroId, search]);

  return { counts, isLoading, error };
}
